import React, { useState } from 'react'

// Utils
import { truncate } from 'utils'
import { humanize } from 'utils'
import lowerCase from 'lodash/lowerCase'

// Helper function to format text safely
const formatText = (
  text: string | undefined,
  length: number = 20,
  camelized?: boolean
): string => {
  if (text === undefined || text === null) {
    return ''
  }

  try {
    return truncate(humanize(text, camelized) ?? '', length)
  } catch (e) {
    return text || ''
  }
}

// Components
import { Checkbox } from '../core/checkbox'
import { Button } from '../core/button'

const ATTRIBUTES_CONTAINER_STYLES =
  'flex flex-shrink-0 flex-col mb-4 box-border m-0'
// Base checkbox container styles
const BASE_CHECKBOX_STYLES =
  'box-border mx-0 flex items-center mb-0.5 py-1 px-2 rounded transition duration-150'

// Function to generate checkbox container styles based on selection state
const getCheckboxStyles = (isSelected: boolean) =>
  isSelected
    ? `${BASE_CHECKBOX_STYLES} bg-blue-50 border border-blue-200`
    : `${BASE_CHECKBOX_STYLES} hover:bg-gray-50`

interface IFilterProps {
  facet: any
  attribute: string
  appliedFacets: string[]
  maxVisibleFilters?: number
  onChange: (value: string) => (checked: boolean) => void
  transformLabel?: string
}

const Filter: React.FC<IFilterProps> = ({
  facet,
  attribute,
  appliedFacets,
  maxVisibleFilters = 5,
  transformLabel,
  onChange: handleCheckBox,
}) => {
  const [showAllOptions, setShowAllOptions] = useState<boolean>(false)

  const title = formatText(transformLabel ?? attribute, 100, true)

  const allKeys: string[] = Object.keys(facet)
  const keysLength: number = allKeys.length

  const facets: string[] = showAllOptions
    ? Object.keys(facet)
    : Object.keys(facet).slice(0, maxVisibleFilters)

  const handleOnToggle = () => setShowAllOptions((prevState) => !prevState)

  return (
    <div key={attribute} className={ATTRIBUTES_CONTAINER_STYLES}>
      <h5 className="text-sm font-bold text-primary mt-2">{title}</h5>
      {facets.map((value: string) => {
        const facetValue = `${attribute}:${value}`
        const isSelected = appliedFacets?.includes(facetValue)
        return (
          <div
            className={getCheckboxStyles(isSelected)}
            key={`${attribute}:${value}`}
          >
            <label
              className="text-sm text-gray-700 flex items-center cursor-pointer w-full"
              htmlFor={value}
            >
              <Checkbox
                value={value}
                id={value}
                name={value}
                checked={appliedFacets?.includes(facetValue)}
                onCheckedChange={handleCheckBox(facetValue)}
                className="mr-2"
              />
              <span className="flex-grow">{formatText(value, 20)}</span>
              <span className="text-xs text-gray-500 ml-1 font-bold">
                ({facet[value]})
              </span>
            </label>
          </div>
        )
      })}
      {keysLength > maxVisibleFilters ? (
        <p style={{ marginTop: '0.5rem' }}>
          <Button
            onClick={handleOnToggle}
            className="cursor-pointer text-actionable text-xs font-bold"
            size="sm"
            variant="ghost"
          >
            {showAllOptions ? 'Shrink' : 'Show all'} {lowerCase(title)}
          </Button>
        </p>
      ) : null}
    </div>
  )
}

export default Filter
